import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { gql } from 'apollo-angular';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';

import { GetCollectionsQuery, SearchProductsQuery } from '../../../common/generated-types';
import { ASSET_FRAGMENT } from '../../../common/graphql/fragments.graphql';
import { DataService } from '../../providers/data/data.service';
import { LanguageService } from '../../providers/language/language.service';

export interface BannerItem {
    id: string;
    title: string;
    description: string;
    buttonLabel: string;
    buttonLink: string;
    asset: {
        id: string;
        preview: string;
        width: number;
        height: number;
        name: string;
        focalPoint?: { x: number; y: number };
    };
}

@Component({
    selector: 'vsf-home-page',
    templateUrl: './home-page.component.html',
    styleUrls: ['./home-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomePageComponent implements OnInit, OnDestroy {

    collections$: Observable<GetCollectionsQuery['collections']['items']>;
    bestSellers$: Observable<SearchProductsQuery['search']['items']>;
    newProducts$: Observable<SearchProductsQuery['search']['items']>;
    specialOffers$: Observable<any[]>;
    brandItems$: Observable<any[]>;
    bannerItems: BannerItem[] = [];
    currentSlide = 0;

    private destroy$ = new Subject<void>();
    private autoSlideInterval: ReturnType<typeof setInterval> | null = null;

    private isNonEnglish: boolean;

    constructor(private dataService: DataService, private cdr: ChangeDetectorRef, private languageService: LanguageService) {
        this.isNonEnglish = this.languageService.currentLanguage !== 'en';
    }

    private fetch<T = any, V extends Record<string, any> = {}>(query: any, variables?: V): Observable<T> {
        return this.isNonEnglish
            ? this.dataService.queryFresh<T, V>(query, variables)
            : this.dataService.query<T, V>(query, variables);
    }

    ngOnInit(): void {
        this.collections$ = this.fetch<GetCollectionsQuery>(GET_COLLECTIONS, {
            options: { take: 50, topLevelOnly: true },
        }).pipe(map(({collections}) => collections.items.filter(c => !(c as any).customFields?.isSector)));

        this.bestSellers$ = this.fetch<any>(GET_BEST_SELLER_PRODUCTS, {
            take: 4,
        }).pipe(map(({ bestSellerProducts }) => this.mapProducts(bestSellerProducts)));

        this.specialOffers$ = this.fetch<any>(GET_DISCOUNTED_PRODUCTS, {
            take: 4,
        }).pipe(map(({ discountedProducts }) =>
            this.mapDiscountedVariants(discountedProducts ?? []).slice(0, 4),
        ));

        this.newProducts$ = this.fetch<SearchProductsQuery>(SEARCH_PRODUCTS, {
            input: { take: 4, groupByProduct: true, sort: { price: 'DESC' } },
        }).pipe(map(data => data.search.items));

        this.brandItems$ = this.fetch<{ brandItems: any[] }>(GET_BRAND_ITEMS, {})
            .pipe(map(data => data.brandItems ?? []));

        this.fetch<{ bannerItems: BannerItem[] }>(GET_BANNER_ITEMS, {})
            .pipe(
                takeUntil(this.destroy$),
                tap(data => {
                    this.bannerItems = data.bannerItems;
                    this.currentSlide = 0;
                    this.startAutoSlide();
                    this.cdr.markForCheck();
                }),
            ).subscribe();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.stopAutoSlide();
    }

    goToSlide(index: number): void {
        this.currentSlide = index;
        this.restartAutoSlide();
    }

    nextSlide(): void {
        if (this.bannerItems.length > 0) {
            this.currentSlide = (this.currentSlide + 1) % this.bannerItems.length;
            this.restartAutoSlide();
        }
    }

    prevSlide(): void {
        if (this.bannerItems.length > 0) {
            this.currentSlide = (this.currentSlide - 1 + this.bannerItems.length) % this.bannerItems.length;
            this.restartAutoSlide();
        }
    }

    private startAutoSlide(): void {
        this.stopAutoSlide();
        if (this.bannerItems.length > 1) {
            this.autoSlideInterval = setInterval(() => {
                this.currentSlide = (this.currentSlide + 1) % this.bannerItems.length;
                this.cdr.markForCheck();
            }, 5000);
        }
    }

    private stopAutoSlide(): void {
        if (this.autoSlideInterval) {
            clearInterval(this.autoSlideInterval);
            this.autoSlideInterval = null;
        }
    }

    private restartAutoSlide(): void {
        this.startAutoSlide();
        this.cdr.markForCheck();
    }

    trackByProductId(index: number, item: any): string {
        return item.productId;
    }

    private mapDiscountedVariants(entries: any[]): any[] {
        const cards: any[] = [];
        for (const entry of entries ?? []) {
            const product = entry?.product;
            if (!product) continue;
            const variants: any[] = product.variants || [];
            const variantById = new Map<string, any>(variants.map((v: any) => [String(v.id), v]));
            const discounted: any[] = entry.discountedVariants || [];
            const variantsToRender: Array<{ variant: any; }> = [];
            if (discounted.length > 0) {
                for (const dv of discounted) {
                    const v = variantById.get(String(dv.variantId));
                    if (v) variantsToRender.push({ variant: v });
                }
            } else if (variants.length > 0) {
                const sorted = [...variants].sort((a, b) => Number(a.priceWithTax) - Number(b.priceWithTax));
                variantsToRender.push({ variant: sorted[0] });
            }
            for (const { variant } of variantsToRender) {
                const price = Number(variant.priceWithTax);
                if (!Number.isFinite(price)) continue;
                cards.push({
                    productId: product.id,
                    productVariantId: variant.id,
                    productName: variant.name || product.name,
                    slug: product.slug,
                    description: product.description,
                    productAsset: variant.featuredAsset || product.featuredAsset,
                    priceWithTax: { min: price, max: price },
                });
            }
        }
        return cards;
    }

    private mapProducts(items: any[]): any[] {
        return items.map(item => {
            const variants = item.variants || [];
            const prices = variants.map((v: any) => v.priceWithTax).filter((p: any) => p != null);
            const minPrice = prices.length > 0 ? Math.min(...prices) : null;
            const maxPrice = prices.length > 0 ? Math.max(...prices) : null;
            return {
                productId: item.id,
                productVariantId: variants[0]?.id || item.id,
                productName: item.name,
                slug: item.slug,
                description: item.description,
                productAsset: item.featuredAsset,
                priceWithTax: minPrice != null ? { min: minPrice, max: maxPrice } : {},
            };
        });
    }
}

const GET_COLLECTIONS = gql`
    query GetCollections($options: CollectionListOptions) {
        collections(options: $options) {
            items {
                id
                name
                slug
                parent {
                    id
                    slug
                    name
                }
                featuredAsset {
                    id
                    preview
                }
            }
        }
    }
`;

const SEARCH_PRODUCTS = gql`
    query SearchProducts($input: SearchInput!) {
        search(input: $input) {
            items {
                productId
                productVariantId
                slug
                productName
                description
                priceWithTax {
                    ... on PriceRange {
                        min
                        max
                    }
                }
                productAsset {
                    id
                    preview
                    focalPoint {
                        x
                        y
                    }
                }
            }
            totalItems
        }
    }
`;

const GET_BEST_SELLER_PRODUCTS = gql`
    query GetBestSellerProducts($take: Int) {
        bestSellerProducts(take: $take) {
            id
            name
            slug
            description
            featuredAsset {
                id
                preview
                focalPoint {
                    x
                    y
                }
            }
            variants {
                id
                priceWithTax
            }
        }
    }
`;

const GET_BANNER_ITEMS = gql`
    query GetBannerItems {
        bannerItems {
            id
            title
            description
            buttonLabel
            buttonLink
            asset {
                ...Asset
            }
        }
    }
    ${ASSET_FRAGMENT}
`;

const GET_BRAND_ITEMS = gql`
    query GetBrandItems {
        brandItems {
            id
            name
            link
            asset {
                preview
                source
            }
        }
    }
`;

const GET_DISCOUNTED_PRODUCTS = gql`
    query GetDiscountedProducts($take: Int) {
        discountedProducts(take: $take) {
            product {
                id
                name
                slug
                featuredAsset {
                    id
                    preview
                    focalPoint {
                        x
                        y
                    }
                }
                variants {
                    id
                    name
                    priceWithTax
                    featuredAsset {
                        id
                        preview
                        focalPoint {
                            x
                            y
                        }
                    }
                }
            }
            discountPercentage
            discountedVariants {
                variantId
                discountPercentage
            }
        }
    }
`;
